import React, { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { DataTable, SubHeading } from "../Components";
import {
  deleteAxios,
  getAxios,
  patchAxios,
  postAxios,
} from "../Services/commonService";
import {
  API_EXCHANGE_LIST,
  API_EXCHANGE_UPDATE,
  API_EXCHANGE_DELETE,
} from "../utils/api/ApiConstant";
import { CommonModal, ConfimationModal, ExchangeCreate } from "../Modal";
import Icon from "../assets/icons/Icon";

const Exchange = () => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");
  const [isShow, setIsShow] = useState({
    isOpen: false,
    rowData: null,
    name: "",
    modalTitle: "",
    modalContent: null,
    apiFlag: false,
  });

  const { isLoading, data: exchangeData } = useQuery({
    queryKey: [
      "exchangeData",
      currentPage,
      recordsPerPage,
      searchTerm,
      isShow.apiFlag,
    ],
    queryFn: async () =>
      await getAxios({
        url: API_EXCHANGE_LIST,
        params: {
          page: currentPage,
          limit: recordsPerPage,
          search: searchTerm,
        },
      }),
  });

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleRecordsperPage = useCallback((value) => {
    setRecordsPerPage(value);
  }, []);

  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: "exchangeName",
        header: "Exchange name",
      },
      {
        accessorKey: "displayOrder",
        header: "disaplay order",
      },
      {
        accessorKey: "via",
        header: "via	",
      },
      {
        accessorKey: "isActive",
        header: "status",
        cell: ({ row }) => (
          <ConfimationModal
            isSwitch={true}
            getValue={row?.original?.isActive}
            onStatusChange={(newValue) =>
              handleUpdateRequest({
                url: API_EXCHANGE_UPDATE,
                rowData: row?.original,
                newValue,
                key: "isActive",
              })
            }
          />
        ),
      },
      {
        accessorKey: "isAutomatic",
        header: "auto update",
        cell: ({ row }) => (
          <ConfimationModal
            isSwitch={true}
            getValue={row?.original?.isAutomatic}
            onStatusChange={(newValue) =>
              handleUpdateRequest({
                url: API_EXCHANGE_UPDATE,
                rowData: row?.original,
                newValue,
                key: "isAutomatic",
              })
            }
          />
        ),
      },
      {
        accessorKey: "isAutomatic",
        header: "auto bhav",
        cell: ({ row }) => (
          <ConfimationModal
            isSwitch={true}
            getValue={row?.original?.isAutomatic}
            onStatusChange={(newValue) =>
              handleUpdateRequest({
                url: API_EXCHANGE_UPDATE,
                rowData: row?.original,
                newValue,
                key: "isAutomatic",
              })
            }
          />
        ),
      },
      {
        accessorKey: "startDate",
        header: "start",
      },
      {
        accessorKey: "endDate",
        header: "end",
      },
      {
        accessorKey: "action",
        header: "action",
        cell: ({ row }) => {
          const handleUpdateModal = useCallback(({ rowData }) => {
            setIsShow({
              isOpen: true,
              rowData,
              name: "edit",
              modalTitle: "Edit Exchange",
              modalContent: ExchangeCreate,
            });
          });

          return (
            <div className="d-flex">
              <Icon
                name="MdEdit"
                className="m-1"
                title="Edit"
                onClick={() => handleUpdateModal({ rowData: row?.original })}
              />

              <ConfimationModal
                isIcon={true}
                iconName="MdDelete"
                getValue={row?.original?.isAutomatic}
                onStatusChange={() =>
                  handleDeleteRequest({
                    rowData: row?.original,
                  })
                }
              />
            </div>
          );
        },
      },
    ],
    []
  );

  const fetchData = useCallback(() => {
    if (!exchangeData) return { data: [], pages: 0 };

    return {
      data: exchangeData.docs,
      pages: exchangeData.totalPages,
    };
  }, [exchangeData]);

  const handleQueryClient = (rowData, updatedRowData, key) => {
    return queryClient.setQueryData(
      ["exchangeData", currentPage, recordsPerPage, searchTerm, isShow.apiFlag],
      (oldData) => {
        return {
          ...oldData,
          docs: oldData?.docs.map((obj) =>
            obj._id === rowData._id
              ? { ...obj, [key]: updatedRowData[key] }
              : obj
          ),
        };
      }
    );
  };

  const handleUpdateRequest = async ({ url, rowData, newValue, key }) => {
    const payload = { ...rowData, [key]: newValue };
    const costumUrl = `${url}/${rowData?._id}`;

    const res = await patchAxios({ url: costumUrl, payload });

    if (res) {
      handleQueryClient(rowData, payload, key);
    }
  };

  const handleDeleteRequest = async ({ rowData }) => {
    const costumUrl = `${API_EXCHANGE_DELETE}/${rowData?._id}`;

    const res = await deleteAxios({
      url: costumUrl,
      payload: {},
    });

    if (res) {
      setIsShow((prev) => ({
        ...prev,
        apiFlag: !prev.apiFlag,
      }));
    }
  };

  return (
    <>
      <SubHeading
        subTitle="Exchange"
        isAddBtn={true}
        handleShowHide={() =>
          setIsShow((prev) => ({
            ...prev,
            isOpen: true,
            rowData: {},
            name: "add",
            modalTitle: "Add Exchange",
            modalContent: ExchangeCreate,
          }))
        }
      />

      <div className="card-body">
        <DataTable
          columns={columns}
          fetchData={fetchData}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onRecordsPerPageChange={handleRecordsperPage}
          recordsPerPage={recordsPerPage}
          onSearchChange={handleSearch}
          isLoading={isLoading}
          isIndexing={true}
          isCheckBox={false}
          handleSearchKey={setIsShow}
        />
      </div>

      {isShow.isOpen && (
        <CommonModal
          isShow={isShow.isOpen}
          handleShowHide={() =>
            setIsShow((prev) => ({
              ...prev,
              isOpen: false,
              rowData: {},
              name: "",
            }))
          }
          modalTitle={isShow.modalTitle}
        >
          {isShow.modalContent &&
            React.createElement(isShow.modalContent, {
              handleShowHide: () =>
                setIsShow((prev) => ({
                  ...prev,
                  isOpen: false,
                  rowData: {},
                  name: "",
                  modalTitle: "",
                  apiFlag: !prev.apiFlag,
                })),
              rowData: isShow.rowData,
            })}
        </CommonModal>
      )}
    </>
  );
};

export default Exchange;
